import React, { useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import PropTypes from 'prop-types';
import { Modal, Button } from 'react-bootstrap';
import linkifyHtml from './linkifyHtml';

// Force all links to open in a new tab with noopener noreferrer
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 's', 'del', 'u', 'a', 'ul', 'ol', 'li', 'p', 'br', 'div', 'span'],
  ALLOWED_ATTR: ['href', 'title', 'target', 'rel'],
  ALLOWED_URI_REGEXP: /^https?:\/\//i,
};

const isSameOrigin = (href) => {
  try {
    return new URL(href, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
};

function MessageContent({ body, contentFormat = 'plain' }) {
  const [pendingUrl, setPendingUrl] = useState(null);

  // A video-only pitch syncs with a null body; never let that stringify to "null".
  const renderedContent = useMemo(() => {
    const text = body ?? '';
    if (contentFormat === 'html') {
      return DOMPurify.sanitize(linkifyHtml(text), SANITIZE_CONFIG);
    }
    return text;
  }, [body, contentFormat]);

  const handleClick = (event) => {
    const link = event.target.closest('a[href]');
    if (!link || !event.currentTarget.contains(link)) {
      return;
    }

    const href = link.getAttribute('href');
    if (isSameOrigin(href)) {
      return;
    }

    event.preventDefault();
    setPendingUrl(href);
  };

  const handleConfirm = () => {
    const url = pendingUrl;
    setPendingUrl(null);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (contentFormat === 'html') {
    return (
      <>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div
          onClick={handleClick}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: renderedContent }}
          className="message-content-html"
        />

        <Modal show={pendingUrl !== null} onHide={() => setPendingUrl(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Open external link</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-secondary mb-2">You&apos;re leaving Qwoted to visit an external link:</p>
            <div className="border rounded p-2 text-break small">{pendingUrl}</div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setPendingUrl(null)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirm}>
              Open link
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }

  // Plain text rendering (backward compatible)
  return <div className="message-content-plain">{renderedContent}</div>;
}

MessageContent.propTypes = {
  body: PropTypes.string,
  contentFormat: PropTypes.oneOf(['plain', 'html']),
};

MessageContent.defaultProps = {
  body: '',
};

export default MessageContent;
